import { useState, useMemo } from "react";
import {
  Button as AntButton,
  message,
  Modal,
  Space,
  Form,
  Input,
  InputNumber,
  Select,
  Tag,
  DatePicker,
  Tooltip,
  Popconfirm,
  Checkbox,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  Landmark,
  Wallet,
  ArrowDownLeft,
  ArrowUpRight,
  CreditCard,
  SlidersHorizontal,
  Banknote,
  Smartphone,
  ShieldCheck,
  Building,
} from "lucide-react";
import type { ColumnsType } from "antd/es/table";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  fetchDealerPaymentAccounts,
  createDealerPaymentAccount,
  updateDealerPaymentAccount,
  deleteDealerPaymentAccount,
  adjustDealerAccountBalance,
  fetchDealerLedger,
  fetchDealerLedgerStats,
  type CreatePaymentAccountInput,
  type AdjustBalanceInput,
} from "../api/auth";
import type { DealerPaymentAccount, DealerLedgerEntry } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { Metric } from "../components/Metric";
import {
  DataTable,
  PageHeader,
  PageShell,
  Panel,
} from "../components/Page";

const { Option } = Select;
const { RangePicker } = DatePicker;

const ACCOUNT_TYPES = [
  { label: "Physical Cash Box / Drawer", value: "CASH" },
  { label: "Bank Account (Current / Savings)", value: "BANK" },
  { label: "bKash Merchant / Agent Wallet", value: "BKASH" },
  { label: "Nagad Business Wallet", value: "NAGAD" },
  { label: "Rocket Account", value: "ROCKET" },
  { label: "Other Financial Channel", value: "OTHER" },
];

export default function FinanceLedgerPage() {
  const { seller } = useAuth();
  const queryClient = useQueryClient();

  // Filter state
  const [page, setPage] = useState(1);
  const [selectedAccountId, setSelectedAccountId] = useState<string | undefined>(undefined);
  const [selectedEntryType, setSelectedEntryType] = useState<string | undefined>(undefined);
  const [selectedSource, setSelectedSource] = useState<string | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);

  // Modals state
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<DealerPaymentAccount | null>(null);
  const [accountSubmitting, setAccountSubmitting] = useState(false);
  const [accountForm] = Form.useForm();

  // Adjust Balance Modal state
  const [adjustModalOpen, setAdjustModalOpen] = useState(false);
  const [adjustTargetAccount, setAdjustTargetAccount] = useState<DealerPaymentAccount | null>(null);
  const [adjustSubmitting, setAdjustSubmitting] = useState(false);
  const [adjustForm] = Form.useForm();
  const [selectedAccountTypeField, setSelectedAccountTypeField] = useState<string>("CASH");

  // Live Queries
  const { data: accounts = [] } = useQuery({
    queryKey: ["dealer-payment-accounts"],
    queryFn: () => fetchDealerPaymentAccounts(true),
    enabled: !!seller,
  });

  const queryParams = useMemo(() => {
    return {
      page,
      limit: 20,
      paymentAccountId: selectedAccountId || undefined,
      entryType: selectedEntryType || undefined,
      source: selectedSource || undefined,
      fromDate: dateRange?.[0] ? dateRange[0].format("YYYY-MM-DD") : undefined,
      toDate: dateRange?.[1] ? dateRange[1].format("YYYY-MM-DD") : undefined,
    };
  }, [page, selectedAccountId, selectedEntryType, selectedSource, dateRange]);

  const {
    data: ledgerData,
    isLoading: ledgerLoading,
    refetch: refetchLedger,
  } = useQuery({
    queryKey: ["dealer-ledger", queryParams],
    queryFn: () => fetchDealerLedger(queryParams),
    enabled: !!seller,
  });

  const { data: stats } = useQuery({
    queryKey: [
      "dealer-ledger-stats",
      selectedAccountId,
      dateRange?.[0]?.format("YYYY-MM-DD"),
      dateRange?.[1]?.format("YYYY-MM-DD"),
    ],
    queryFn: () =>
      fetchDealerLedgerStats({
        paymentAccountId: selectedAccountId || undefined,
        fromDate: dateRange?.[0] ? dateRange[0].format("YYYY-MM-DD") : undefined,
        toDate: dateRange?.[1] ? dateRange[1].format("YYYY-MM-DD") : undefined,
      }),
    enabled: !!seller,
  });

  // Account Modal handlers
  const handleOpenCreateAccount = () => {
    setEditingAccount(null);
    accountForm.resetFields();
    accountForm.setFieldsValue({
      accountType: "BANK",
      initialBalance: 0,
      isDefault: false,
    });
    setSelectedAccountTypeField("BANK");
    setAccountModalOpen(true);
  };

  const handleOpenEditAccount = (acc: DealerPaymentAccount) => {
    setEditingAccount(acc);
    accountForm.resetFields();
    accountForm.setFieldsValue({
      name: acc.name,
      accountType: acc.accountType,
      accountNumber: acc.accountNumber || undefined,
      bankName: acc.bankName || undefined,
      branchName: acc.branchName || undefined,
      routingNumber: acc.routingNumber || undefined,
      isDefault: acc.isDefault,
      isActive: acc.isActive,
    });
    setSelectedAccountTypeField(acc.accountType);
    setAccountModalOpen(true);
  };

  const handleSubmitAccount = async (values: any) => {
    setAccountSubmitting(true);
    try {
      if (editingAccount) {
        await updateDealerPaymentAccount(editingAccount.id, {
          name: values.name?.trim(),
          accountType: values.accountType,
          accountNumber: values.accountNumber?.trim() || undefined,
          bankName: values.bankName?.trim() || undefined,
          branchName: values.branchName?.trim() || undefined,
          routingNumber: values.routingNumber?.trim() || undefined,
          isDefault: values.isDefault,
          isActive: values.isActive,
        });
        message.success("Payment account updated successfully");
      } else {
        const payload: CreatePaymentAccountInput = {
          name: values.name.trim(),
          accountType: values.accountType,
          accountNumber: values.accountNumber?.trim() || undefined,
          bankName: values.bankName?.trim() || undefined,
          branchName: values.branchName?.trim() || undefined,
          routingNumber: values.routingNumber?.trim() || undefined,
          initialBalance: Number(values.initialBalance || 0),
          isDefault: Boolean(values.isDefault),
        };
        await createDealerPaymentAccount(payload);
        message.success("Payment account created and ledger initialized");
      }

      setAccountModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ["dealer-payment-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["dealer-ledger"] });
      queryClient.invalidateQueries({ queryKey: ["dealer-ledger-stats"] });
    } catch (err: any) {
      message.error(err?.message || "Failed to save payment account");
    } finally {
      setAccountSubmitting(false);
    }
  };

  const handleDeleteAccount = async (id: string) => {
    try {
      await deleteDealerPaymentAccount(id);
      message.success("Payment account deactivated");
      queryClient.invalidateQueries({ queryKey: ["dealer-payment-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["dealer-ledger"] });
      queryClient.invalidateQueries({ queryKey: ["dealer-ledger-stats"] });
    } catch (err: any) {
      message.error(err?.message || "Failed to deactivate account");
    }
  };

  // Adjust Balance Handlers
  const handleOpenAdjust = (acc: DealerPaymentAccount) => {
    setAdjustTargetAccount(acc);
    adjustForm.resetFields();
    adjustForm.setFieldsValue({
      adjustmentType: "DEPOSIT",
      amount: undefined,
      title: "Opening cash / capital injection",
    });
    setAdjustModalOpen(true);
  };

  const handleSubmitAdjust = async (values: any) => {
    if (!adjustTargetAccount) return;
    setAdjustSubmitting(true);
    try {
      const payload: AdjustBalanceInput = {
        amount: Number(values.amount),
        adjustmentType: values.adjustmentType,
        title: values.title.trim(),
        description: values.description?.trim() || undefined,
      };
      await adjustDealerAccountBalance(adjustTargetAccount.id, payload);
      message.success("Account balance adjusted and ledger entry recorded");
      setAdjustModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ["dealer-payment-accounts"] });
      queryClient.invalidateQueries({ queryKey: ["dealer-ledger"] });
      queryClient.invalidateQueries({ queryKey: ["dealer-ledger-stats"] });
    } catch (err: any) {
      message.error(err?.message || "Failed to adjust balance");
    } finally {
      setAdjustSubmitting(false);
    }
  };

  // Reset Filters
  const handleResetFilters = () => {
    setSelectedAccountId(undefined);
    setSelectedEntryType(undefined);
    setSelectedSource(undefined);
    setDateRange(null);
    setPage(1);
  };

  // Ledger Table Columns
  const columns: ColumnsType<DealerLedgerEntry> = [
    {
      title: "Date & Time",
      key: "entryDate",
      width: 170,
      render: (_, r) => (
        <div>
          <div className="font-medium text-stone-800 text-xs">
            {dayjs(r.entryDate).format("MMM DD, YYYY")}
          </div>
          <div className="text-[11px] text-stone-500 font-mono">
            {dayjs(r.createdAt).format("hh:mm A")}
          </div>
        </div>
      ),
    },
    {
      title: "Transaction & Source",
      key: "title",
      render: (_, r) => {
        let tagColor = "blue";
        let sourceLabel: string = r.source;
        if (r.source === "SALE") {
          tagColor = "green";
          sourceLabel = "VEHICLE SALE";
        } else if (r.source === "OFFICE_EXPENSE") {
          tagColor = "volcano";
          sourceLabel = "EXPENSE";
        } else if (r.source === "DEPOSIT") {
          tagColor = "cyan";
          sourceLabel = "DEPOSIT";
        } else if (r.source === "WITHDRAWAL") {
          tagColor = "orange";
          sourceLabel = "WITHDRAWAL";
        } else if (r.source === "ADJUSTMENT") {
          tagColor = "purple";
          sourceLabel = "ADJUSTMENT";
        }

        return (
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-stone-900 text-sm">{r.title}</span>
              <Tag color={tagColor} className="text-[10px] uppercase font-semibold leading-4 px-1.5 py-0 rounded">
                {sourceLabel}
              </Tag>
            </div>
            {r.description && (
              <div className="text-xs text-stone-500 mt-0.5 line-clamp-1">{r.description}</div>
            )}
            {r.referenceId && (
              <div className="text-[11px] text-stone-400 font-mono mt-0.5">
                Ref ID: {r.referenceId}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Payment Account",
      key: "account",
      width: 180,
      render: (_, r) => (
        <div>
          <div className="font-medium text-stone-800 text-xs flex items-center gap-1.5">
            <Wallet className="h-3.5 w-3.5 text-stone-500" />
            <span>{r.paymentAccount?.name || "Payment Account"}</span>
          </div>
          <div className="text-[11px] text-stone-500 mt-0.5 font-mono">
            {r.paymentAccount?.accountNumber ? `A/C: ${r.paymentAccount.accountNumber}` : r.paymentAccount?.accountType}
          </div>
        </div>
      ),
    },
    {
      title: "Credit (+)",
      key: "credit",
      align: "right",
      width: 130,
      render: (_, r) =>
        r.entryType === "CREDIT" ? (
          <span className="font-bold text-emerald-600 text-sm flex items-center justify-end gap-1">
            <ArrowUpRight className="h-3.5 w-3.5" />
            +৳{Number(r.amount).toLocaleString()}
          </span>
        ) : (
          <span className="text-stone-300 text-xs">—</span>
        ),
    },
    {
      title: "Debit (-)",
      key: "debit",
      align: "right",
      width: 130,
      render: (_, r) =>
        r.entryType === "DEBIT" ? (
          <span className="font-bold text-rose-600 text-sm flex items-center justify-end gap-1">
            <ArrowDownLeft className="h-3.5 w-3.5" />
            -৳{Number(r.amount).toLocaleString()}
          </span>
        ) : (
          <span className="text-stone-300 text-xs">—</span>
        ),
    },
    {
      title: "Running Balance",
      key: "balanceAfter",
      align: "right",
      width: 140,
      render: (_, r) => (
        <div>
          <span className="font-bold text-stone-900 text-sm font-mono">
            ৳{Number(r.balanceAfter).toLocaleString()}
          </span>
          <div className="text-[10px] text-stone-400">
            prev: ৳{Number(r.balanceBefore).toLocaleString()}
          </div>
        </div>
      ),
    },
  ];

  return (
    <PageShell>
      <PageHeader
        title="Finance & Accounts Ledger"
        description="Dynamic dealer payment accounts, sales credits, expense debits, and unified audit ledger"
        actions={
          <Space>
            <AntButton
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleOpenCreateAccount}
              className="bg-accent hover:bg-accent-hover"
            >
              Add Payment Account
            </AntButton>
          </Space>
        }
      />

      {/* ── Metric Summary Cards ────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Metric
          label="Total Liquid Cash"
          value={`৳${(stats?.totalCashInAccounts ?? 0).toLocaleString()}`}
          hint={`Across ${stats?.accountCount ?? accounts.filter((a) => a.isActive).length} active accounts`}
        />
        <Metric
          label="Total Sales Credits"
          value={`৳${(stats?.totalSalesRevenue ?? 0).toLocaleString()}`}
          hint={`${stats?.salesCount ?? 0} bike sales credited`}
        />
        <Metric
          label="Office Expenses Debited"
          value={`৳${(stats?.totalExpensesOutflow ?? 0).toLocaleString()}`}
          hint={`${stats?.expensesCount ?? 0} office expense vouchers`}
        />
        <Metric
          label="Total Ledger Flow"
          value={`৳${((stats?.totalCredits ?? 0) - (stats?.totalDebits ?? 0)).toLocaleString()}`}
          hint={`Credits: ৳${(stats?.totalCredits ?? 0).toLocaleString()} | Debits: ৳${(stats?.totalDebits ?? 0).toLocaleString()}`}
        />
      </div>

      {/* ── Dynamic Payment Accounts Showcase ────────────────────────────── */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Building className="h-4 w-4 text-stone-700" />
            <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wide">
              Showroom Payment Accounts
            </h3>
            <span className="text-xs text-stone-500">
              ({accounts.length} configured)
            </span>
          </div>
          <span className="text-xs text-stone-500">
            Click &quot;Adjust&quot; to deposit float, withdraw cash, or reconcile balance
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {accounts.map((acc) => {
            const isCash = acc.accountType === "CASH";
            const isBank = acc.accountType === "BANK";
            const isMfs = ["BKASH", "NAGAD", "ROCKET"].includes(acc.accountType);

            return (
              <div
                key={acc.id}
                className={`relative rounded-xl border p-4 transition-all duration-150 ${
                  acc.isActive
                    ? "bg-white border-stone-200 hover:border-stone-300 hover:shadow-sm"
                    : "bg-stone-50 border-stone-200 opacity-60"
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2.5">
                    <div
                      className={`h-9 w-9 rounded-lg flex items-center justify-center ${
                        isCash
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                          : isBank
                            ? "bg-blue-50 text-blue-700 border border-blue-100"
                            : isMfs
                              ? "bg-rose-50 text-rose-700 border border-rose-100"
                              : "bg-purple-50 text-purple-700 border border-purple-100"
                      }`}
                    >
                      {isCash && <Banknote className="h-5 w-5" />}
                      {isBank && <Landmark className="h-5 w-5" />}
                      {isMfs && <Smartphone className="h-5 w-5" />}
                      {!isCash && !isBank && !isMfs && <CreditCard className="h-5 w-5" />}
                    </div>
                    <div>
                      <h4 className="font-semibold text-stone-900 text-sm leading-tight flex items-center gap-1.5">
                        {acc.name}
                        {acc.isDefault && (
                          <Tag color="gold" className="text-[10px] font-bold uppercase leading-4 px-1 py-0 mr-0">
                            Default
                          </Tag>
                        )}
                        {!acc.isActive && (
                          <Tag color="default" className="text-[10px] leading-4 px-1 py-0 mr-0">
                            Inactive
                          </Tag>
                        )}
                      </h4>
                      <p className="text-[11px] text-stone-500 font-mono mt-0.5">
                        {acc.bankName ? `${acc.bankName}${acc.branchName ? ` (${acc.branchName})` : ""}` : acc.accountType}
                        {acc.accountNumber && ` • A/C: ${acc.accountNumber}`}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="my-3 pt-2 border-t border-stone-100">
                  <span className="text-[11px] uppercase tracking-wider text-stone-500 block font-medium">
                    Available Balance
                  </span>
                  <div className="text-2xl font-bold text-stone-900 tracking-tight font-mono">
                    ৳{Number(acc.currentBalance).toLocaleString()}
                    <span className="text-xs font-normal text-stone-500 ml-1.5 font-sans">BDT</span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-stone-100">
                  <AntButton
                    size="small"
                    icon={<SlidersHorizontal className="h-3 w-3 inline mr-1" />}
                    onClick={() => handleOpenAdjust(acc)}
                    disabled={!acc.isActive}
                    className="text-xs text-stone-700 border-stone-300 hover:border-stone-400"
                  >
                    Adjust Balance
                  </AntButton>

                  <Space size="small">
                    <Tooltip title="Edit Account">
                      <AntButton
                        type="text"
                        size="small"
                        icon={<EditOutlined className="text-stone-500 hover:text-stone-800" />}
                        onClick={() => handleOpenEditAccount(acc)}
                      />
                    </Tooltip>
                    {!acc.isDefault && acc.isActive && (
                      <Tooltip title="Deactivate Account">
                        <Popconfirm
                          title="Deactivate Account"
                          description="Past transactions remain saved in ledger. You can reactivate anytime."
                          okText="Deactivate"
                          cancelText="Cancel"
                          onConfirm={() => handleDeleteAccount(acc.id)}
                        >
                          <AntButton
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                          />
                        </Popconfirm>
                      </Tooltip>
                    )}
                  </Space>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Financial Flow Ledger Audit Table ────────────────────────────── */}
      <Panel>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-base font-bold text-stone-900 flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
              General Financial Ledger
            </h3>
            <p className="text-xs text-stone-500 mt-0.5">
              Immutable chronological record of every bike sale, expense debit, cash deposit, and withdrawal
            </p>
          </div>

          <AntButton
            icon={<ReloadOutlined />}
            size="small"
            onClick={() => {
              refetchLedger();
              queryClient.invalidateQueries({ queryKey: ["dealer-ledger-stats"] });
              queryClient.invalidateQueries({ queryKey: ["dealer-payment-accounts"] });
            }}
          >
            Refresh
          </AntButton>
        </div>

        {/* Filter bar */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 p-3 bg-stone-50 rounded-lg border border-stone-200 mb-4 text-xs">
          <div>
            <label className="block text-[11px] font-medium text-stone-600 mb-1">Filter Account</label>
            <Select
              className="w-full text-xs"
              placeholder="All Accounts"
              allowClear
              value={selectedAccountId}
              onChange={(val) => {
                setSelectedAccountId(val);
                setPage(1);
              }}
            >
              {accounts.map((a) => (
                <Option key={a.id} value={a.id}>
                  {a.name} (৳{Number(a.currentBalance).toLocaleString()})
                </Option>
              ))}
            </Select>
          </div>

          <div>
            <label className="block text-[11px] font-medium text-stone-600 mb-1">Flow Direction</label>
            <Select
              className="w-full text-xs"
              placeholder="Credits & Debits"
              allowClear
              value={selectedEntryType}
              onChange={(val) => {
                setSelectedEntryType(val);
                setPage(1);
              }}
            >
              <Option value="CREDIT">Credits (+) Inflow</Option>
              <Option value="DEBIT">Debits (-) Outflow</Option>
            </Select>
          </div>

          <div>
            <label className="block text-[11px] font-medium text-stone-600 mb-1">Source / Event</label>
            <Select
              className="w-full text-xs"
              placeholder="All Sources"
              allowClear
              value={selectedSource}
              onChange={(val) => {
                setSelectedSource(val);
                setPage(1);
              }}
            >
              <Option value="SALE">Vehicle Sales</Option>
              <Option value="OFFICE_EXPENSE">Office Expenses</Option>
              <Option value="DEPOSIT">Direct Deposits</Option>
              <Option value="WITHDRAWAL">Cash Withdrawals</Option>
              <Option value="ADJUSTMENT">Adjustments / Reconciliations</Option>
            </Select>
          </div>

          <div>
            <label className="block text-[11px] font-medium text-stone-600 mb-1">Date Range</label>
            <RangePicker
              className="w-full text-xs"
              value={dateRange}
              onChange={(dates) => {
                setDateRange(dates as any);
                setPage(1);
              }}
            />
          </div>

          <div className="flex items-end">
            <AntButton
              onClick={handleResetFilters}
              className="w-full text-xs border-stone-300 hover:border-stone-400"
            >
              Reset Filters
            </AntButton>
          </div>
        </div>

        <DataTable<DealerLedgerEntry>
          columns={columns}
          rows={ledgerData?.items ?? []}
          loading={ledgerLoading}
          total={ledgerData?.total ?? 0}
          page={page}
          pageSize={20}
          onPageChange={(p: number) => setPage(p)}
          rowKey="id"
          emptyText="No ledger records found"
        />
      </Panel>

      {/* ── Modal: Add / Edit Payment Account ────────────────────────────── */}
      <Modal
        title={editingAccount ? "Edit Payment Account" : "Create New Payment Account"}
        open={accountModalOpen}
        onCancel={() => setAccountModalOpen(false)}
        footer={null}
        width={540}
        destroyOnClose
      >
        <Form
          form={accountForm}
          layout="vertical"
          onFinish={handleSubmitAccount}
          className="pt-2"
        >
          <Form.Item
            label="Account Name / Label"
            name="name"
            rules={[{ required: true, message: "Please provide an account label" }]}
            extra="e.g. City Bank Dealership A/C, Main Cash Box, bKash Merchant"
          >
            <Input placeholder="Enter distinct account label" />
          </Form.Item>

          <Form.Item
            label="Account Type"
            name="accountType"
            rules={[{ required: true }]}
          >
            <Select
              onChange={(val) => setSelectedAccountTypeField(val)}
              placeholder="Select account classification"
            >
              {ACCOUNT_TYPES.map((t) => (
                <Option key={t.value} value={t.value}>
                  {t.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Conditional Fields based on Account Type */}
          {selectedAccountTypeField === "BANK" && (
            <div className="p-3 bg-stone-50 rounded-lg border border-stone-200 mb-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Form.Item
                  label="Bank Name"
                  name="bankName"
                  rules={[{ required: true, message: "Bank name is required" }]}
                >
                  <Input placeholder="e.g. City Bank, BRAC Bank" />
                </Form.Item>

                <Form.Item label="Branch Name" name="branchName">
                  <Input placeholder="e.g. Motijheel, Gulshan-2" />
                </Form.Item>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Form.Item
                  label="Account Number"
                  name="accountNumber"
                  rules={[{ required: true, message: "Account number is required" }]}
                >
                  <Input placeholder="e.g. 1102938475001" />
                </Form.Item>

                <Form.Item label="Routing Number" name="routingNumber">
                  <Input placeholder="e.g. 090271829" />
                </Form.Item>
              </div>
            </div>
          )}

          {["BKASH", "NAGAD", "ROCKET"].includes(selectedAccountTypeField) && (
            <Form.Item
              label="Wallet / Account Mobile Number"
              name="accountNumber"
              rules={[{ required: true, message: "Mobile wallet number is required" }]}
            >
              <Input placeholder="e.g. 017xxxxxxxx (Merchant / Personal)" />
            </Form.Item>
          )}

          {!editingAccount && (
            <Form.Item
              label="Opening Initial Balance (BDT)"
              name="initialBalance"
              extra="Initial starting cash/bank float. Creates an opening CREDIT ledger entry."
            >
              <InputNumber
                className="w-full"
                min={0}
                prefix="৳"
                placeholder="0"
              />
            </Form.Item>
          )}

          <div className="py-2 border-t border-stone-100">
            <Form.Item name="isDefault" valuePropName="checked" className="mb-0">
              <Checkbox>Set as Default Payment Account for Sales & Expenses</Checkbox>
            </Form.Item>
          </div>

          <div className="mt-6 flex justify-end gap-2 border-t border-stone-200 pt-4">
            <AntButton onClick={() => setAccountModalOpen(false)}>Cancel</AntButton>
            <AntButton
              type="primary"
              htmlType="submit"
              loading={accountSubmitting}
              className="bg-accent hover:bg-accent-hover"
            >
              {editingAccount ? "Update Account" : "Save Payment Account"}
            </AntButton>
          </div>
        </Form>
      </Modal>

      {/* ── Modal: Adjust Balance (Deposit / Withdrawal / Reconcile) ──────── */}
      <Modal
        title={`Adjust Balance: ${adjustTargetAccount?.name || ""}`}
        open={adjustModalOpen}
        onCancel={() => setAdjustModalOpen(false)}
        footer={null}
        width={480}
        destroyOnClose
      >
        {adjustTargetAccount && (
          <div className="mb-4 p-3 bg-stone-50 rounded-lg border border-stone-200 flex justify-between items-center text-xs">
            <div>
              <span className="text-stone-500 block">Current Account Balance</span>
              <span className="font-bold text-stone-900 text-lg font-mono">
                ৳{Number(adjustTargetAccount.currentBalance).toLocaleString()} BDT
              </span>
            </div>
            <Tag color="blue">{adjustTargetAccount.accountType}</Tag>
          </div>
        )}

        <Form
          form={adjustForm}
          layout="vertical"
          onFinish={handleSubmitAdjust}
        >
          <Form.Item
            label="Adjustment Action"
            name="adjustmentType"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="DEPOSIT">
                <span className="text-emerald-700 font-medium">Deposit (+) Cash In / Capital Injection</span>
              </Option>
              <Option value="WITHDRAWAL">
                <span className="text-rose-700 font-medium">Withdrawal (-) Owner Draw / Bank Transfer Out</span>
              </Option>
              <Option value="ADJUSTMENT">
                <span className="text-indigo-700 font-medium">Reconciliation / Physical Count Correction</span>
              </Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Amount (BDT)"
            name="amount"
            rules={[{ required: true, message: "Please specify positive amount" }]}
          >
            <InputNumber
              className="w-full"
              min={0.01}
              prefix="৳"
              placeholder="e.g. 50000"
            />
          </Form.Item>

          <Form.Item
            label="Title / Summary"
            name="title"
            rules={[{ required: true, message: "Please enter a brief title" }]}
          >
            <Input placeholder="e.g. Added showroom float, Capital added by partner" />
          </Form.Item>

          <Form.Item label="Reason / Notes (Optional)" name="description">
            <Input.TextArea
              rows={2}
              placeholder="Provide any context for audit trail..."
            />
          </Form.Item>

          <div className="mt-6 flex justify-end gap-2 border-t border-stone-200 pt-4">
            <AntButton onClick={() => setAdjustModalOpen(false)}>Cancel</AntButton>
            <AntButton
              type="primary"
              htmlType="submit"
              loading={adjustSubmitting}
              className="bg-accent hover:bg-accent-hover"
            >
              Apply Balance & Record in Ledger
            </AntButton>
          </div>
        </Form>
      </Modal>
    </PageShell>
  );
}
